/**
 * Boot-time sanity check for env vars critical to the payment flow.
 *
 * CRA inlines `process.env.REACT_APP_*` at build time. If a var was missing
 * at build, references resolve to `undefined`, recipient `to:` becomes
 * `undefined`, ethers throws a non-specific error and the user sees the
 * generic "Error sending transaction. Please try again later." catch-all.
 *
 * This module:
 *   1. Logs a loud console.warn at boot if any required var is missing.
 *   2. Exposes `getPaymentEnvSnapshot()` so logPaymentError can attach the
 *      presence map to every payment-error record we send to Firestore.
 */

type EnvSpec = {
  key: string;
  usage: string;
  required: boolean;
};

const PAYMENT_ENV_SPECS: EnvSpec[] = [
  { key: "REACT_APP_V2E", usage: "VoteToEarn API base URL", required: true },
  { key: "REACT_APP_API", usage: "Default axios base URL", required: true },
  { key: "REACT_APP_ETH_ACCOUNT_KEY", usage: "ETH/ERC20 recipient address", required: true },
  { key: "REACT_APP_BNB_ACCOUNT_KEY", usage: "BNB/BEP20 recipient address", required: true },
  { key: "REACT_APP_MATIC_ACCOUNT_KEY", usage: "Polygon recipient address", required: true },
  { key: "REACT_APP_TON_ACCOUNT_KEY", usage: "TON recipient address", required: false },
  { key: "REACT_APP_TRON_ACCOUNT_KEY", usage: "Tron recipient address", required: false },
  { key: "REACT_APP_FIREBASE_API_KEY", usage: "Firebase auth", required: true },
  { key: "REACT_APP_FIREBASE_PROJECT_ID", usage: "Firebase project", required: true },
];

const readEnv = (key: string): string | undefined => {
  const v = (process.env as Record<string, string | undefined>)[key];
  if (v === undefined) return undefined;
  const trimmed = String(v).trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

/** Returns a presence-only snapshot ({ KEY: true|false }) safe to log. */
export const getPaymentEnvSnapshot = (): Record<string, boolean> => {
  const snap: Record<string, boolean> = {};
  for (const spec of PAYMENT_ENV_SPECS) {
    snap[spec.key] = readEnv(spec.key) !== undefined;
  }
  return snap;
};

const runBootCheck = (): void => {
  const missingRequired: EnvSpec[] = [];
  const missingOptional: EnvSpec[] = [];
  for (const spec of PAYMENT_ENV_SPECS) {
    if (readEnv(spec.key) === undefined) {
      (spec.required ? missingRequired : missingOptional).push(spec);
    }
  }

  if (missingRequired.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      "[env-sanity] MISSING required payment env vars — payment flows will fail:\n" +
        missingRequired.map((s) => `  - ${s.key}  (${s.usage})`).join("\n")
    );
  }
  if (missingOptional.length > 0) {
    // eslint-disable-next-line no-console
    console.info(
      "[env-sanity] Optional payment env vars not set:\n" +
        missingOptional.map((s) => `  - ${s.key}  (${s.usage})`).join("\n")
    );
  }
};

runBootCheck();
